import React, { useState, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EmailIcon from "@mui/icons-material/Email";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import logo from "../images/Extractor logo.png";
import { fontFamily } from "../components/Constants/constants";
import InstaColors from "../components/Constants/constants";
import { BASE_URL } from "../App_Settings/app_settings";
import "./VerifyAccount.css";

const RESEND_TIMER_SECONDS = 60;
const OTP_LENGTH = 6;

const VerifyAccount = () => {
  const history = useHistory();
  const location = useLocation();

  // Get data passed from registration page
  const email = location.state?.email || "";
  const mobileNumber = location.state?.mobileNumber || "";
  const firstName = location.state?.firstName || "";

  // Redirect if no email (user navigated here directly)
  useEffect(() => {
    if (!email) {
      history.replace("/login");
    }
  }, [email, history]);

  // Email verification state
  const [emailToken, setEmailToken] = useState("");
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [emailSuccess, setEmailSuccess] = useState("");

  // Mobile verification state
  const [otp, setOtp] = useState(new Array(OTP_LENGTH).fill(""));
  const [isMobileVerified, setIsMobileVerified] = useState(false);
  const [isVerifyingMobile, setIsVerifyingMobile] = useState(false);
  const [mobileError, setMobileError] = useState("");
  const [mobileSuccess, setMobileSuccess] = useState("");

  // Resend timers
  const [canResendEmail, setCanResendEmail] = useState(false);
  const [emailResendTimer, setEmailResendTimer] = useState(RESEND_TIMER_SECONDS);
  const [canResendMobile, setCanResendMobile] = useState(false);
  const [mobileResendTimer, setMobileResendTimer] = useState(RESEND_TIMER_SECONDS);

  // Resend loading states
  const [isResendingEmail, setIsResendingEmail] = useState(false);
  const [isResendingMobile, setIsResendingMobile] = useState(false);

  // Both verified state
  const [allVerified, setAllVerified] = useState(false);

  // OTP input refs
  const otpRefs = useRef([]);

  // Email resend timer countdown
  useEffect(() => {
    let interval = null;
    if (emailResendTimer > 0 && !canResendEmail && !isEmailVerified) {
      interval = setInterval(() => {
        setEmailResendTimer((prev) => {
          if (prev <= 1) {
            setCanResendEmail(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [emailResendTimer, canResendEmail, isEmailVerified]);

  // Mobile resend timer countdown
  useEffect(() => {
    let interval = null;
    if (mobileResendTimer > 0 && !canResendMobile && !isMobileVerified) {
      interval = setInterval(() => {
        setMobileResendTimer((prev) => {
          if (prev <= 1) {
            setCanResendMobile(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [mobileResendTimer, canResendMobile, isMobileVerified]);

  // Check if both are verified
  useEffect(() => {
    if (isEmailVerified && isMobileVerified) {
      setAllVerified(true);
    }
  }, [isEmailVerified, isMobileVerified]);

  // ─── Email Verification ───────────────────────────────────────────

  const handleVerifyEmail = async () => {
    if (!emailToken.trim()) {
      setEmailError("Please enter the verification token from your email.");
      return;
    }

    setIsVerifyingEmail(true);
    setEmailError("");
    setEmailSuccess("");

    try {
      const response = await fetch(`${BASE_URL}/Users/verifyemail`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: emailToken.trim(), email }),
      });

      if (response.ok) {
        const data = await response.json();
        setIsEmailVerified(true);
        setEmailSuccess(data.message || "Email verified successfully!");
        setEmailError("");

        // Check if account is now fully activated
        if (data.accountActivated) {
          setIsMobileVerified(true);
          setAllVerified(true);
        }
      } else {
        const errorData = await response.json().catch(() => null);
        setEmailError(
          errorData?.message || "Invalid or expired verification token. Please try again."
        );
      }
    } catch (error) {
      console.error("Email verification error:", error);
      setEmailError("An error occurred. Please try again.");
    } finally {
      setIsVerifyingEmail(false);
    }
  };

  // ─── Mobile Verification ──────────────────────────────────────────

  const handleOtpChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setMobileError("");

    // Auto-focus next input
    if (value && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (pastedData.length > 0) {
      const newOtp = [...otp];
      for (let i = 0; i < pastedData.length; i++) {
        newOtp[i] = pastedData[i];
      }
      setOtp(newOtp);
      // Focus the next empty input or the last one
      const nextEmpty = pastedData.length < OTP_LENGTH ? pastedData.length : OTP_LENGTH - 1;
      otpRefs.current[nextEmpty]?.focus();
    }
  };

  const handleVerifyMobile = async () => {
    const otpValue = otp.join("");
    if (otpValue.length !== OTP_LENGTH) {
      setMobileError("Please enter the complete 6-digit OTP.");
      return;
    }

    setIsVerifyingMobile(true);
    setMobileError("");
    setMobileSuccess("");

    try {
      const response = await fetch(`${BASE_URL}/Users/verifymobile`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp: otpValue }),
      });

      if (response.ok) {
        const data = await response.json();
        setIsMobileVerified(true);
        setMobileSuccess(data.message || "Mobile verified successfully!");
        setMobileError("");

        // Check if account is now fully activated
        if (data.accountActivated) {
          setIsEmailVerified(true);
          setAllVerified(true);
        }
      } else {
        const errorData = await response.json().catch(() => null);
        setMobileError(errorData?.message || "Invalid or expired OTP. Please try again.");
        // Clear OTP on error
        setOtp(new Array(OTP_LENGTH).fill(""));
        otpRefs.current[0]?.focus();
      }
    } catch (error) {
      console.error("Mobile verification error:", error);
      setMobileError("An error occurred. Please try again.");
    } finally {
      setIsVerifyingMobile(false);
    }
  };

  // ─── Resend Handlers ──────────────────────────────────────────────

  const handleResendEmail = async () => {
    setIsResendingEmail(true);
    setEmailError("");
    setEmailSuccess("");

    try {
      const response = await fetch(`${BASE_URL}/Users/resendverification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, type: "email" }),
      });

      if (response.ok) {
        setEmailSuccess("Verification email resent. Please check your inbox.");
        setCanResendEmail(false);
        setEmailResendTimer(RESEND_TIMER_SECONDS);
      } else {
        const errorData = await response.json().catch(() => null);
        setEmailError(errorData?.message || "Failed to resend email. Please try again.");
      }
    } catch (error) {
      console.error("Resend email error:", error);
      setEmailError("An error occurred. Please try again.");
    } finally {
      setIsResendingEmail(false);
    }
  };

  const handleResendMobile = async () => {
    setIsResendingMobile(true);
    setMobileError("");
    setMobileSuccess("");

    try {
      const response = await fetch(`${BASE_URL}/Users/resendverification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, type: "mobile" }),
      });

      if (response.ok) {
        setMobileSuccess("OTP resent to your mobile number.");
        setCanResendMobile(false);
        setMobileResendTimer(RESEND_TIMER_SECONDS);
        // Clear current OTP
        setOtp(new Array(OTP_LENGTH).fill(""));
        otpRefs.current[0]?.focus();
      } else {
        const errorData = await response.json().catch(() => null);
        setMobileError(errorData?.message || "Failed to resend OTP. Please try again.");
      }
    } catch (error) {
      console.error("Resend mobile error:", error);
      setMobileError("An error occurred. Please try again.");
    } finally {
      setIsResendingMobile(false);
    }
  };

  // ─── Navigate to Login ────────────────────────────────────────────

  const handleGoToLogin = () => {
    history.push({
      pathname: "/login",
      state: { someValue: true },
    });
  };

  // ─── Render ───────────────────────────────────────────────────────

  if (!email) return null;

  return (
    <div
      className="login-div"
      style={{
        backgroundImage: `url(${process.env.PUBLIC_URL}/background.png)`,
      }}
    >
      <div className="verify-container card">
        <img className="logo" src={logo} alt="logo" />

        {!allVerified ? (
          <>
            <h2
              style={{
                fontFamily,
                color: InstaColors.fontColorDarkGray,
                fontSize: "20px",
                fontWeight: 600,
                marginBottom: "4px",
              }}
            >
              Verify Your Account
            </h2>
            <p
              style={{
                fontFamily,
                color: InstaColors.fontColorGray,
                fontSize: "13px",
                textAlign: "center",
                marginBottom: "24px",
                padding: "0 20px",
              }}
            >
              Hi {firstName || "there"}, please verify your email and mobile number to activate your
              account.
            </p>

            {/* ─── Email Verification Section ─── */}
            <div className="verify-section">
              <div className="verify-section-header">
                <EmailIcon
                  style={{
                    fontSize: 20,
                    color: isEmailVerified ? "#4caf50" : "#2aa7e0",
                    marginRight: 8,
                  }}
                />
                <span
                  style={{
                    fontFamily,
                    fontWeight: 600,
                    fontSize: "14px",
                    color: InstaColors.fontColorDarkGray,
                  }}
                >
                  Email Verification
                </span>
                {isEmailVerified && (
                  <CheckCircleIcon style={{ fontSize: 18, color: "#4caf50", marginLeft: 8 }} />
                )}
              </div>

              {!isEmailVerified ? (
                <>
                  <p
                    style={{
                      fontFamily,
                      fontSize: "12px",
                      color: InstaColors.fontColorGray,
                      marginBottom: "10px",
                    }}
                  >
                    Enter the verification token sent to <strong>{email}</strong>
                  </p>

                  <div className="verify-input-row">
                    <input
                      type="text"
                      className="verify-token-input"
                      placeholder="Enter verification token"
                      value={emailToken}
                      onChange={(e) => {
                        setEmailToken(e.target.value);
                        setEmailError("");
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && emailToken.trim()) {
                          handleVerifyEmail();
                        }
                      }}
                      disabled={isVerifyingEmail}
                      style={{ fontFamily }}
                    />
                    <button
                      className="verify-btn"
                      onClick={handleVerifyEmail}
                      disabled={isVerifyingEmail || !emailToken.trim()}
                    >
                      {isVerifyingEmail ? (
                        <CircularProgress size={16} style={{ color: "#fff" }} />
                      ) : (
                        "Verify"
                      )}
                    </button>
                  </div>

                  {emailError && (
                    <Alert severity="error" sx={{ fontSize: "12px", py: 0, mt: 1, width: "100%" }}>
                      {emailError}
                    </Alert>
                  )}
                  {emailSuccess && (
                    <Alert
                      severity="success"
                      sx={{ fontSize: "12px", py: 0, mt: 1, width: "100%" }}
                    >
                      {emailSuccess}
                    </Alert>
                  )}

                  <div className="verify-resend-row">
                    {canResendEmail ? (
                      <button
                        className="verify-resend-btn"
                        onClick={handleResendEmail}
                        disabled={isResendingEmail}
                      >
                        {isResendingEmail ? "Sending..." : "Resend Email"}
                      </button>
                    ) : (
                      <span
                        style={{
                          fontFamily,
                          fontSize: "12px",
                          color: InstaColors.fontColorGray,
                        }}
                      >
                        Resend in {emailResendTimer}s
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <Alert severity="success" sx={{ fontSize: "12px", py: 0, mt: 1, width: "100%" }}>
                  {emailSuccess || "Email verified successfully!"}
                </Alert>
              )}
            </div>

            {/* ─── Mobile Verification Section ─── */}
            <div className="verify-section">
              <div className="verify-section-header">
                <PhoneAndroidIcon
                  style={{
                    fontSize: 20,
                    color: isMobileVerified ? "#4caf50" : "#2aa7e0",
                    marginRight: 8,
                  }}
                />
                <span
                  style={{
                    fontFamily,
                    fontWeight: 600,
                    fontSize: "14px",
                    color: InstaColors.fontColorDarkGray,
                  }}
                >
                  Mobile Verification
                </span>
                {isMobileVerified && (
                  <CheckCircleIcon style={{ fontSize: 18, color: "#4caf50", marginLeft: 8 }} />
                )}
              </div>

              {!isMobileVerified ? (
                <>
                  <p
                    style={{
                      fontFamily,
                      fontSize: "12px",
                      color: InstaColors.fontColorGray,
                      marginBottom: "10px",
                    }}
                  >
                    Enter the 6-digit OTP sent to <strong>{mobileNumber || "your mobile"}</strong>
                  </p>

                  <div className="verify-otp-row">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (otpRefs.current[index] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        className="verify-otp-input"
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        onPaste={index === 0 ? handleOtpPaste : undefined}
                        disabled={isVerifyingMobile}
                        style={{ fontFamily }}
                      />
                    ))}
                    <button
                      className="verify-btn"
                      onClick={handleVerifyMobile}
                      disabled={isVerifyingMobile || otp.join("").length !== OTP_LENGTH}
                    >
                      {isVerifyingMobile ? (
                        <CircularProgress size={16} style={{ color: "#fff" }} />
                      ) : (
                        "Verify"
                      )}
                    </button>
                  </div>

                  {mobileError && (
                    <Alert severity="error" sx={{ fontSize: "12px", py: 0, mt: 1, width: "100%" }}>
                      {mobileError}
                    </Alert>
                  )}
                  {mobileSuccess && (
                    <Alert
                      severity="success"
                      sx={{ fontSize: "12px", py: 0, mt: 1, width: "100%" }}
                    >
                      {mobileSuccess}
                    </Alert>
                  )}

                  <div className="verify-resend-row">
                    {canResendMobile ? (
                      <button
                        className="verify-resend-btn"
                        onClick={handleResendMobile}
                        disabled={isResendingMobile}
                      >
                        {isResendingMobile ? "Sending..." : "Resend OTP"}
                      </button>
                    ) : (
                      <span
                        style={{
                          fontFamily,
                          fontSize: "12px",
                          color: InstaColors.fontColorGray,
                        }}
                      >
                        Resend in {mobileResendTimer}s
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <Alert severity="success" sx={{ fontSize: "12px", py: 0, mt: 1, width: "100%" }}>
                  {mobileSuccess || "Mobile verified successfully!"}
                </Alert>
              )}
            </div>
          </>
        ) : (
          /* ─── All Verified - Success Screen ─── */
          <div className="verify-success">
            <CheckCircleIcon style={{ fontSize: 64, color: "#4caf50", marginBottom: 16 }} />
            <h2
              style={{
                fontFamily,
                color: InstaColors.fontColorDarkGray,
                fontSize: "20px",
                fontWeight: 600,
                marginBottom: "8px",
              }}
            >
              Account Verified!
            </h2>
            <p
              style={{
                fontFamily,
                color: InstaColors.fontColorGray,
                fontSize: "13px",
                textAlign: "center",
                marginBottom: "24px",
              }}
            >
              Your email and mobile number have been verified successfully. Your account is now
              active.
            </p>
            <button className="verify-login-btn" onClick={handleGoToLogin}>
              Continue to Login
            </button>
          </div>
        )}

        {/* Back to Login link (shown only during verification) */}
        {!allVerified && (
          <p
            style={{
              fontFamily,
              fontSize: "12px",
              color: InstaColors.fontColorGray,
              marginTop: "16px",
              marginBottom: "20px",
              cursor: "pointer",
            }}
            onClick={handleGoToLogin}
          >
            ← Back to Login
          </p>
        )}
      </div>
    </div>
  );
};

export default VerifyAccount;
